import { Form } from "../../core/Form";
import { IntlPhoneWrapper } from "../../components/IntlPhoneWrapper";
import * as Modal from "../../utils/Modal";
import { t } from "../../utils/I18n";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── 다중 파일 슬롯 ───────────────────────────────────────────────────────────

function makeRemoveBtn(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "sec-join-form-band__file-remove";
    btn.setAttribute("aria-label", t("join.remove_file"));
    btn.innerHTML = '<i class="fa-regular fa-xmark" aria-hidden="true"></i>';
    return btn;
}

function wireSlotInput(slot: HTMLElement, noFileTxt: string): void {
    const input   = slot.querySelector<HTMLInputElement>("[data-file-input]");
    const nameEl  = slot.querySelector<HTMLElement>("[data-file-name]");
    const trigger = slot.querySelector<HTMLButtonElement>("[data-file-trigger]");
    if (!input || !nameEl) return;
    // Choose file 버튼 클릭 → 숨겨진 input.click() 으로 파일 선택기 열기
    // (label[for] 를 제거했으므로 JS로 직접 트리거)
    trigger?.addEventListener("click", () => input.click());
    input.addEventListener("change", () => {
        nameEl.textContent = input.files?.[0]?.name ?? noFileTxt;
    });
}

function wireFileSlots(form: HTMLFormElement): void {
    const wrapper = form.querySelector<HTMLElement>("[data-file-slots]");
    if (!wrapper) return;

    const list    = wrapper.querySelector<HTMLElement>("[data-file-list]");
    const addBtn  = wrapper.querySelector<HTMLButtonElement>("[data-file-add]");
    const maxFiles = parseInt(wrapper.dataset.maxFiles ?? "5", 10);
    // i18n: "No file selected" 텍스트를 첫 슬롯 data-file-name 에서 읽는다.
    const noFileTxt = list?.querySelector<HTMLElement>("[data-file-name]")?.textContent?.trim() ?? "No file selected";

    if (!list || !addBtn) return;

    // 첫 슬롯 input 이벤트 등록
    const firstSlot = list.querySelector<HTMLElement>("[data-file-slot]");
    if (firstSlot) wireSlotInput(firstSlot, noFileTxt);

    const updateAddBtn = (): void => {
        const count = list.querySelectorAll("[data-file-slot]").length;
        addBtn.disabled = count >= maxFiles;
        addBtn.style.display = count >= maxFiles ? "none" : "";
    };

    addBtn.addEventListener("click", () => {
        const slots = list.querySelectorAll<HTMLElement>("[data-file-slot]");
        if (slots.length >= maxFiles) return;

        const idx  = slots.length;
        const tmpl = slots[0].cloneNode(true) as HTMLElement;

        // 파일 input 초기화 & id 교체 (label[for] 없음 — JS trigger 방식)
        const newInput = tmpl.querySelector<HTMLInputElement>("[data-file-input]");
        const newName  = tmpl.querySelector<HTMLElement>("[data-file-name]");
        if (newInput) {
            newInput.value = "";
            newInput.id    = `join_resume_${idx}`;
        }
        if (newName)  newName.textContent = noFileTxt;

        // ✕ 제거 버튼 추가
        const removeBtn = makeRemoveBtn();
        removeBtn.addEventListener("click", () => {
            tmpl.remove();
            updateAddBtn();
        });
        tmpl.appendChild(removeBtn);

        wireSlotInput(tmpl, noFileTxt);
        list.appendChild(tmpl);
        updateAddBtn();
    });

    updateAddBtn();
}

// ─── Privacy Policy 모달 ──────────────────────────────────────────────────────
// 동의 본문은 form-privacy-agree 가 렌더한 [data-privacy-content] <template> 에서 읽는다.
// (전역 legal/privacy fetch 폐기 — 이 폼 전용 수집·이용 안내를 inline 으로 제공.)

function wirePrivacyModal(scope: HTMLElement): void {
    const tpl = scope.querySelector<HTMLTemplateElement>("[data-privacy-content]");
    scope.querySelectorAll<HTMLButtonElement>("[data-privacy-modal-open]").forEach((btn) => {
        btn.addEventListener("click", async () => {
            const html = tpl?.innerHTML.trim() ?? "";
            if (!html) return;
            await Modal.content(html, btn.textContent?.trim() || t("join.privacy_policy"));
        });
    });
}

// ─── 에러 메시지 파싱 ────────────────────────────────────────────────────────

function pickFirstErrorMessage(data: Record<string, unknown> | undefined, fallback: string): string {
    if (!data) return fallback;
    const message = typeof data.message === "string" ? data.message : "";
    const errors = data.errors as Record<string, string[]> | null | undefined;
    if (errors && typeof errors === "object") {
        for (const key of Object.keys(errors)) {
            const arr = errors[key];
            if (Array.isArray(arr) && arr.length > 0) {
                return message || arr[0];
            }
        }
    }
    return message || fallback;
}

// ─── 컨트롤러 진입점 ─────────────────────────────────────────────────────────

export const JoinController = {
    index(): void {
        const form = document.querySelector<HTMLFormElement>("[data-join-form]");
        if (!form) return;

        // 다중 파일 슬롯
        wireFileSlots(form);

        // Privacy Policy 모달 트리거
        wirePrivacyModal(form);

        // intl-tel-input 국제 전화번호 입력
        // 기본 국가는 서버에서 locale→flag 변환 후 data-phone-country 로 전달 (join-form-band.php)
        const phoneInput   = form.querySelector<HTMLInputElement>("[data-join-phone]");
        const phoneCountry = form.dataset.phoneCountry ?? "kr";
        let itiWrapper: IntlPhoneWrapper | null = null;
        if (phoneInput) {
            itiWrapper = new IntlPhoneWrapper(phoneInput, {
                initialCountry: phoneCountry,
                separateDialCode: true,
            }).init();

            // 드롭다운 열릴 때 .iti__country-list 에 data-lenis-prevent 부여 (절대규칙 #12)
            phoneInput.addEventListener("open:countrydropdown", () => {
                document.querySelector<HTMLElement>(".iti__country-list")
                    ?.setAttribute("data-lenis-prevent", "");
            });
        }

        new Form(form, {
            submitButtonSelector: 'button[type="submit"]',
            loaderSelector: "[data-form-loader]",
            submittingText: t("join.submitting"),
            validate: (f) => {
                const get = (sel: string) =>
                    f.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(sel);

                const checks: Array<[string, string, () => boolean]> = [
                    ["#join_first_name", t("join.first_name_required"), () => (get("#join_first_name")?.value ?? "").trim() !== ""],
                    ["#join_last_name",  t("join.last_name_required"),  () => (get("#join_last_name")?.value ?? "").trim() !== ""],
                    ["#join_email",      t("join.email_required"),      () => (get("#join_email")?.value ?? "").trim() !== ""],
                    ["#join_email",      t("join.email_invalid"),       () => EMAIL_PATTERN.test((get("#join_email")?.value ?? "").trim())],
                    ["#join_phone",      t("join.phone_required"),      () => {
                        const val = itiWrapper ? itiWrapper.getValue() : (get("#join_phone")?.value ?? "");
                        return val.trim() !== "";
                    }],
                    ['input[name="privacy_agree"]:checked', t("join.privacy_required"), () => !!f.querySelector('input[name="privacy_agree"]:checked')],
                ];

                for (const [selector, msg, ok] of checks) {
                    if (!ok()) {
                        const focusSel = selector.replace(":checked", "");
                        (f.querySelector<HTMLElement>(focusSel))?.focus();
                        return msg;
                    }
                }

                if (itiWrapper && itiWrapper.getValue().trim() !== "" && !itiWrapper.isValid()) {
                    phoneInput?.focus();
                    return t("join.phone_invalid");
                }

                return null;
            },
            onSuccess: async (data) => {
                const msg = (data?.message as string) ?? t("join.success");
                await Modal.alert(msg, t("join.success_title"), { onOk: () => location.reload() });
            },
            onFail: (message, data) => {
                window.alert(pickFirstErrorMessage(data, message || t("join.error")));
            },
            requestFailedMessage: t("join.network_error"),
        }).init();
    },

    submit(): void {},
};
